import { Headphones, Video } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "./Reveal";

const VU_BARS = [0, 1, 2, 3, 4, 5, 6, 7, 8];

const VUColor = ["#C18C3D", "#A3562A", "#4A5844"];

const FloatBadge = ({ testid, className, delay, children }) => (
  <span
    data-testid={testid}
    className={`icon-bob absolute z-10 w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-ink/90 backdrop-blur-sm border border-gold/60 flex items-center justify-center text-gold shadow-xl ${className}`}
    style={{ animationDelay: delay }}
  >
    {children}
  </span>
);

const StudioLevels = () => (
  <div
    data-testid="studio-levels-panel"
    className="absolute -bottom-8 -right-4 lg:-right-8 w-40 lg:w-52 clip-frame-tl border border-amber/50 shadow-2xl bg-tile/95 backdrop-blur-sm p-4 lg:p-5"
  >
    <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-gold mb-3 text-center">
      Studio Levels
    </p>
    <div className="flex items-end justify-center gap-1.5 h-24 lg:h-28">
      {VU_BARS.map((i) => (
        <span
          key={i}
          className="vu-bar"
          style={{
            animationDelay: `${i * 0.12}s`,
            animationDuration: `${0.7 + (i % 4) * 0.25}s`,
          }}
        />
      ))}
    </div>
    <div className="flex justify-center gap-2.5 mt-4">
      {VUColor.map((c, i) => (
        <span
          key={c}
          className="vu-light"
          style={{ backgroundColor: c, animationDelay: `${i * 0.45}s` }}
        />
      ))}
    </div>
  </div>
);

const About = () => (
  <section id="about" data-testid="about-section" className="relative bg-aboutbg py-28 lg:py-40">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20">
      <div className="lg:col-span-5">
        <Reveal>
          <Eyebrow index="01" label="About" />
        </Reveal>
        <h2 className="mt-8 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
          <MaskedLine delay={0.05}>Art was always</MaskedLine>
          <MaskedLine delay={0.15}>
            the choice — <em className="text-gold not-italic font-serif italic">it was</em>
          </MaskedLine>
          <MaskedLine delay={0.25}>
            <em className="font-serif italic text-gold">an atmosphere.</em>
          </MaskedLine>
        </h2>

        <Reveal delay={0.3} className="mt-14 relative">
          <FloatBadge testid="about-badge-headphones" className="-top-5 -left-4" delay="0s">
            <Headphones size={22} strokeWidth={1.75} />
          </FloatBadge>
          <FloatBadge testid="about-badge-camera" className="-top-5 -right-4" delay="1.6s">
            <Video size={22} strokeWidth={1.75} />
          </FloatBadge>
          <div className="relative clip-frame border border-gold/40">
            <img
              src="https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/a72a80d86fe0dc88_dm%20sing.webp"
              alt="Dani Morr singing in the studio"
              className="w-full h-[340px] lg:h-[420px] object-cover analog-grade"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-aboutbg/60 to-transparent" />
            <span className="absolute bottom-4 left-5 font-mono text-[10px] tracking-[0.25em] text-cream/70">
              THE STUDIO — LIVE LEVELS
            </span>
          </div>
          <StudioLevels />
        </Reveal>
      </div>

      <div className="lg:col-span-7 flex flex-col justify-center gap-8 lg:pl-8">
        <Reveal delay={0.1}>
          <p className="text-base lg:text-lg text-cream/85 leading-relaxed">
            Raised in an environment steeped in recording equipment and endless creative
            output, Dani Morr learned early how to transform ideas into tangible
            audio-visual worlds.
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="text-base lg:text-lg text-cream/70 leading-relaxed">
            As a music producer, lyricist, director, and video editor, Dani operates with
            total creative independence. Her music blends R&amp;B, neo-soul, jazz-hop, and
            raw hip-hop lyricism, marked by articulate storytelling and lush organic
            textures. Visually, her aesthetic leans heavily into retro-futurism, vintage
            analog warmth, 90s CRT television textures, VHS scan lines, and grounded
            dark-fantasy worldbuilding.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <p className="text-base lg:text-lg text-cream/70 leading-relaxed">
            Every project is built from the ground up — from writing the first chord
            progressions and engineering stems in the studio, to scriptwriting, directing,
            and editing visual projects framed like distinct television broadcasts and
            narrative cinema.{" "}
            <span className="text-cream font-medium">
              Dani Morr doesn't just create tracks or videos; she builds self-contained
              artistic realms.
            </span>
          </p>
        </Reveal>

        <Reveal delay={0.34}>
          <div className="mt-4 grid grid-cols-3 gap-6 border-t border-cream/15 pt-8" data-testid="about-disciplines">
            {[
              ["Sound", "R&B / Neo-Soul / Jazz-Hop"],
              ["Vision", "Retro-Futurist Cinema"],
              ["Craft", "Fully Independent"],
            ].map(([title, sub]) => (
              <div key={title}>
                <p className="font-serif italic text-xl text-gold">{title}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-2">{sub}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default About;
